import { getContact } from "src/api/chat";

// Supported file extensions for media attachments
const MEDIA_FILE_EXTENSIONS = [
  ".pdf",
  ".jpg",
  ".jpeg",
  ".png",
  ".gif",
  ".bmp",
  ".webp",
  ".csv",
  ".xls",
  ".xlsx",
  ".doc",
  ".docx",
  ".txt",
  ".zip",
  ".rar",
];

// Keywords to skip when detecting phone numbers in messages
const PHONE_DETECTION_SKIP_KEYWORDS = [
  ...MEDIA_FILE_EXTENSIONS,
  "http",
  "https",
  "Download:",
  "www.",
  ".com",
  ".in",
  ".org",
];

// Media types configuration
const MEDIA_TYPES = {
  IMAGE: ["jpg", "jpeg", "png", "gif", "bmp", "webp"],
  DOCUMENT: ["pdf", "doc", "docx", "txt"],
  SPREADSHEET: ["csv", "xls", "xlsx"],
  ARCHIVE: ["zip", "rar"],
};

export function useChatMessageUtils() {
  const isReceivedMessage = (message) => {
    return message.conversation.uniqueName.includes(message.author);
  };

  const isMediaMessage = (message) => !!message?.attributes?.media;
  const isSharedContact = (body) => {
    if (!body) return false;

    const text = body.toLowerCase();

    return /(contact\s+shared|contacts\s+shared|contact\s*\d+:)/i.test(text);
  };

  const normalizePhoneNumber = (rawNumber) => {
    const digits = rawNumber.replaceAll(/\D/g, "");

    if (digits.length === 10) {
      return { normalized: `+91${digits}`, plain: digits };
    } else if (digits.length === 12 && digits.startsWith("91")) {
      return { normalized: `+${digits}`, plain: digits.slice(2) };
    }

    return { normalized: "", plain: "" };
  };

  const getPhoneRegex = () => /(?:\+91[\s-]*)?(\d{3}[\s-]?\d{3}[\s-]?\d{4})/;

  const getSkipKeywords = () => PHONE_DETECTION_SKIP_KEYWORDS;

  const hasPhoneNumber = (text) => {
    if (!text) return false;

    const skipKeywords = getSkipKeywords();

    if (skipKeywords.some((kw) => text.includes(kw))) return false;

    const regex = getPhoneRegex();

    return regex.test(text);
  };

  const detectPhoneInText = (text) => {
    if (!hasPhoneNumber(text)) return null;

    const regex = getPhoneRegex();
    const match = text.match(regex);

    if (match) {
      let rawNumber = match[1].replaceAll(/\D/g, "");
      const { normalized, plain } = normalizePhoneNumber(rawNumber);
      const namePart = text.replace(match[0], "").trim();

      return {
        phone: normalized,
        phonePlain: plain,
        name: namePart || "",
      };
    }

    return null;
  };

  const getExistingContact = async (number) => {
    try {
      const response = await getContact({ number });
      const res = response.data?.data;

      // API returns an array, check if array has items
      const exists = Array.isArray(res) && res.length > 0;
      const userData = exists ? res[0] : null;

      return { exists, userData };
    } catch {
      return { exists: false, userData: null };
    }
  };

  // Check if a file extension is a supported media type
  const isMediaFile = (filename) => {
    if (!filename) return false;

    const extension = filename.toLowerCase().split(".").pop();

    return MEDIA_FILE_EXTENSIONS.some((ext) => ext.includes(extension));
  };

  // Get media type category for a file
  const getMediaType = (filename) => {
    if (!filename) return null;

    const extension = filename.toLowerCase().split(".").pop();

    for (const [type, extensions] of Object.entries(MEDIA_TYPES)) {
      if (extensions.includes(extension)) {
        return type;
      }
    }

    return "OTHER";
  };

  return {
    isReceivedMessage,
    isMediaMessage,
    isSharedContact,
    normalizePhoneNumber,
    getSkipKeywords,
    getPhoneRegex,
    hasPhoneNumber,
    detectPhoneInText,
    getExistingContact,
    isMediaFile,
    getMediaType,
  };
}
